import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ScoreView } from "./ScoreView";
import { useScorePlayback } from "./useScorePlayback";
import { generateExercise, specForScore } from "./generator";
import { keyLabel } from "./transpose";
import { useMidiInput, type MidiInputNote } from "@/audio/midiInput";
import { Keyboard } from "@/components/Keyboard";
import { useMicNotes } from "@/audio/useMicNotes";
import {
  initSequence, feedSequence, parsePitch, accuracy as calcAccuracy, stars as calcStars,
  type SequenceState,
} from "@/grading/grade";
import type { Score } from "./types";

/**
 * Sight-read mode: four bars the student has never seen, one pass, no restarts.
 *
 * The rules are the discipline. A student who can restart is practising a piece,
 * not sight-reading it — so the exercise is revealed, counted in, played once,
 * and then scored. Pressing "Another" throws it away and generates a new one,
 * which is the only honest way to practise this skill.
 */

type Phase = "ready" | "reading" | "done";

export interface SightReadPanelProps {
  /** The piece being studied; the exercise is graded to match it. */
  score: Score;
  accentHex: string;
}

export function SightReadPanel({ score, accentHex }: SightReadPanelProps) {
  const [seed, setSeed] = useState(() => Math.floor(Math.random() * 2 ** 31));
  const [phase, setPhase] = useState<Phase>("ready");
  const [revealed, setRevealed] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const startedAt = useRef<number>(0);
  const [lookahead, setLookahead] = useState<number | null>(null);


  const exercise = useMemo(() => {
    try {
      return generateExercise({ ...specForScore(score), seed });
    } catch {
      return null;
    }
  }, [score, seed]);

  const playback = useScorePlayback(exercise ?? score, {
    bpm: 66,
    loop: false,
    countIn: true,
    metronome: true,
  });

  /**
   * What the student is expected to play, in order: the exercise's own notes.
   * Scoring is wait-mode — the cursor only advances on the right note, so a
   * wrong key is counted against accuracy but never derails the reading.
   * Matching is by pitch class, so an octave slip still reads as correct.
   */
  const targetPitches = useMemo(
    () => (exercise?.notes ?? [])
      .filter((n) => !n.rest && n.pitch)
      .sort((a, b) => a.start - b.start)
      .map((n) => n.pitch as string),
    [exercise],
  );

  const [graded, setGraded] = useState<SequenceState>(() => initSequence(targetPitches));
  // Authoritative copy: grading must not run inside a set-state updater
  // (StrictMode double-invokes those, which would double-count every note).
  const gradedRef = useRef(graded);
  const [lastHit, setLastHit] = useState<"correct" | "wrong" | null>(null);
  const hitTimer = useRef<number | null>(null);

  useEffect(() => {
    gradedRef.current = initSequence(targetPitches);
    setGraded(gradedRef.current);
    setLastHit(null);
  }, [targetPitches]);

  /** Grade one played note. Web MIDI and the on-screen keys both land here,
   *  so a student on Safari — which has never shipped Web MIDI — is scored by
   *  exactly the same engine as one with a piano plugged in. */
  const feedNote = useCallback((midiNum: number, pc: number) => {
    if (gradedRef.current.done) return;
    const { state, verdict } = feedSequence(gradedRef.current, { midi: midiNum, pc });
    gradedRef.current = state;
    setGraded(state);
    setLastHit(verdict);
    if (hitTimer.current != null) window.clearTimeout(hitTimer.current);
    hitTimer.current = window.setTimeout(() => setLastHit(null), 220);
  }, []);

  const midi = useMidiInput({
    onNoteOn: (n: MidiInputNote) => feedNote(n.midi, n.pc),
  });

  /** The microphone path — an acoustic piano, or any browser without Web MIDI
   *  (Safari, iPad). Same grader, so the score means the same thing. */
  const mic = useMicNotes((n) => feedNote(n.midi, n.pc));
  const scoringLive = midi.status === "ready" || mic.status === "listening";

  const gradeAcc = calcAccuracy(graded.hits, graded.errors);
  const gradeStars = calcStars(gradeAcc);

  const begin = useCallback(async () => {
    setRevealed(true);
    setPhase("reading");
    startedAt.current = performance.now();
    setLookahead(null);
    await playback.play();
  }, [playback]);

  const finish = useCallback(() => {
    playback.stop();
    setPhase("done");
    setAttempts((a) => a + 1);
  }, [playback]);

  const another = useCallback(() => {
    playback.stop();
    setSeed(Math.floor(Math.random() * 2 ** 31));
    setRevealed(false);
    setPhase("ready");
    setLookahead(null);
  }, [playback]);

  if (!exercise) {
    return (
      <p className="rounded-xl border border-zinc-700 bg-zinc-900/60 p-4 text-sm text-zinc-400">
        This piece's range is too narrow to generate an exercise from. Try a
        different piece.
      </p>
    );
  }

  const bars = exercise.bars;
  const totalBeats = bars * exercise.time[0];
  const progress = phase === "reading"
    ? Math.min(1, playback.beat / (totalBeats * (4 / exercise.time[1])))
    : phase === "done" ? 1 : 0;

  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-zinc-100">Sight-read</h3>
          <p className="text-sm text-zinc-400">
            {bars} bars in {keyLabel(exercise.key)}, {exercise.time[0]}/{exercise.time[1]}.
            Never seen before. One pass, and don't stop to fix anything.
          </p>
        </div>
        <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">
          exercise #{seed.toString(36)}
        </span>
      </header>

      {/* ---- the keyboard, and what it is hearing ----
           Web MIDI needs a real click AND the browser's permission, so this
           says which of those is missing rather than failing silently. ---- */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-zinc-800 bg-zinc-900 px-4 py-3">
        <div className="flex items-center gap-3">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{
              background: midi.status === "ready" ? "#22c55e"
                : midi.status === "denied" ? "#f43f5e" : "#52525b",
              boxShadow: midi.status === "ready" ? "0 0 8px #22c55e88" : undefined,
            }}
            aria-hidden
          />
          <span className="text-sm text-zinc-300">
            {midi.status === "ready" ? (
              <>Listening to <strong className="text-zinc-100">{midi.devices[0] ?? "your keyboard"}</strong>
                {midi.devices.length === 0 && " — no instrument found yet, switch your piano on"}</>
            ) : midi.status === "unsupported" ? (
              "Safari and Firefox can't read a MIDI keyboard — open this lesson in Chrome to be scored."
            ) : midi.status === "blocked-by-frame" ? (
              <>Your keyboard can't be reached from inside the lesson frame.{" "}
                <a
                  href={typeof window !== "undefined" ? window.location.href : "#"}
                  target="_blank" rel="noreferrer"
                  className="font-semibold text-zinc-100 underline underline-offset-2"
                >Open this piece in its own tab</a>{" "}to be scored.</>
            ) : midi.status === "denied" ? (
              <>Chrome is blocking MIDI for this site. Click the{" "}
                <strong className="text-zinc-100">icon at the left of the address bar</strong> →
                Site settings → set <strong className="text-zinc-100">MIDI device control</strong> to
                Allow → reload. Then press Connect again.</>
            ) : (
              "Switch your piano on, then connect it to be scored as you read."
            )}
          </span>
        </div>
        {midi.status !== "ready" && midi.status !== "unsupported" && midi.status !== "blocked-by-frame" && (
          <button
            onClick={() => void midi.enable()}
            disabled={midi.status === "requesting"}
            className="rounded-full border border-zinc-700 px-4 py-1.5 text-[13px] font-semibold text-zinc-200 transition hover:bg-zinc-800 disabled:opacity-50"
          >
            {midi.status === "requesting" ? "Connecting…" : "Connect keyboard"}
          </button>
        )}
        {midi.status === "ready" && (
          <span className="font-mono text-[12px] tabular-nums text-zinc-400">
            {graded.hits}/{targetPitches.length} · {gradeAcc}%
          </span>
        )}
      </div>

      <div
        className="relative rounded-xl border border-zinc-800 bg-[#F6F6FA] p-3"
        style={{ minHeight: 180 }}
      >
        {revealed ? (
          <ScoreView
            score={exercise}
            playingIndex={
              !graded.done && (scoringLive || graded.hits + graded.errors > 0)
                ? graded.cursor
                : phase === "reading" ? playback.playingIndex : null
            }
            showLyrics={false}
            barsPerLine={bars}
          />
        ) : (
          <div className="flex h-[168px] flex-col items-center justify-center gap-3 text-center">
            <p className="max-w-sm text-sm text-zinc-600">
              The music appears when you press start, with one bar of count-in.
              Read straight through — keep your eyes ahead of your hands.
            </p>
            <button
              onClick={begin}
              className="rounded-full px-6 py-2.5 text-sm font-semibold text-zinc-950 transition hover:brightness-110"
              style={{ background: accentHex }}
            >
              Start the pass
            </button>
          </div>
        )}
      </div>

      {(scoringLive || graded.hits + graded.errors > 0) && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-400">
              Your reading
            </span>
            <span
              className="text-[13px] font-semibold"
              style={{ color: lastHit === "wrong" ? "#f43f5e" : lastHit === "correct" ? "#22c55e" : "#71717a" }}
            >
              {lastHit === "wrong" ? "not that one" : lastHit === "correct" ? "yes" : "waiting for the first note"}
            </span>
          </div>

          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-zinc-800">
            <div
              className="h-full rounded-full transition-[width] duration-100"
              style={{
                width: `${targetPitches.length ? (graded.cursor / targetPitches.length) * 100 : 0}%`,
                background: accentHex,
              }}
            />
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-[13px] text-zinc-400">
            <span><strong className="text-emerald-400">{graded.hits}</strong> right</span>
            <span><strong className="text-rose-400">{graded.errors}</strong> missed</span>
            <span>accuracy <strong className="text-zinc-200">{gradeAcc}%</strong></span>
            {graded.done && (
              <span className="tracking-widest text-amber-300">
                {"★".repeat(gradeStars)}<span className="text-zinc-700">{"★".repeat(3 - gradeStars)}</span>
              </span>
            )}
          </div>

          {graded.done && (
            <p className="mt-2 text-[13px] text-zinc-400">
              {gradeStars === 3 ? "Clean pass. Take a new exercise a notch faster."
                : gradeStars === 2 ? "Solid. The misses were the jumps — read a beat ahead."
                : "Slow the tempo and read the shape before you play. Accuracy first."}
            </p>
          )}
        </div>
      )}

      {/* ---- being scored without Web MIDI ----
           Safari has never implemented Web MIDI and Firefox needs an add-on,
           so a keyboard-less path is the only way every student can be
           scored. Same grader, same stars — you tap instead of play. ---- */}
      {midi.status !== "ready" && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-400">
              {mic.status === "listening" ? "Listening to your piano" : "No MIDI keyboard?"}
            </span>
            {mic.status === "listening" ? (
              <span className="flex items-center gap-3">
                <span className="font-mono text-[12px] tabular-nums text-zinc-400">
                  {mic.liveMidi != null ? midiName(mic.liveMidi) : "—"}
                </span>
                <button onClick={mic.stop}
                  className="rounded-full border border-zinc-700 px-3 py-1 text-[12px] text-zinc-300 hover:bg-zinc-800">
                  Stop mic
                </button>
              </span>
            ) : (
              <button
                onClick={() => void mic.start()}
                disabled={mic.status === "starting"}
                className="rounded-full px-4 py-1.5 text-[13px] font-semibold text-zinc-950 transition hover:brightness-110 disabled:opacity-50"
                style={{ background: accentHex }}
              >
                {mic.status === "starting" ? "Opening mic…" : "🎤 Score me by ear"}
              </button>
            )}
          </div>
          {mic.error && <p className="mb-3 text-[12px] text-rose-300">{mic.error}</p>}
          <p className="mb-3 text-[12px] leading-relaxed text-zinc-500">
            {mic.status === "listening"
              ? "Play the exercise on any piano — acoustic or digital. Every browser can do this, including Safari and iPad."
              : "Play an acoustic piano into your microphone, or tap the keys below. Both are scored the same way."}
          </p>
          <Keyboard
            startOctave={4}
            octaves={2}
            height={128}
            highlights={
              !graded.done && graded.steps[graded.cursor]
                ? [{ pitch: graded.steps[graded.cursor].pitch, color: accentHex, label: "▶" }]
                : []
            }
            onPlay={(pitch) => {
              const parsed = parsePitch(pitch);
              if (parsed) feedNote(parsed.midi, parsed.pc);
            }}
          />
        </div>
      )}

      {phase !== "ready" && (
        <div className="h-1.5 overflow-hidden rounded-full bg-zinc-800">
          <div
            className="h-full rounded-full transition-[width] duration-100"
            style={{ width: `${progress * 100}%`, background: accentHex }}
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {phase === "reading" && (
          <>
            <button
              onClick={finish}
              className="rounded-full border border-zinc-600 px-5 py-2 text-sm font-medium text-zinc-200 hover:bg-zinc-800"
            >
              Done
            </button>
            <p className="text-sm text-zinc-400">
              Keep going to the end even if you slip. Stopping is the habit we're
              breaking.
            </p>
          </>
        )}

        {phase === "done" && (
          <>
            <button
              onClick={another}
              className="rounded-full px-5 py-2 text-sm font-semibold text-zinc-950 transition hover:brightness-110"
              style={{ background: accentHex }}
            >
              Another one
            </button>
            <button
              onClick={begin}
              className="rounded-full border border-zinc-600 px-5 py-2 text-sm font-medium text-zinc-200 hover:bg-zinc-800"
            >
              Hear it again
            </button>
            <p className="text-sm text-zinc-400">
              {attempts === 1
                ? "That's one. The next one will be different music."
                : `${attempts} passes today. Every one has been new music.`}
            </p>
          </>
        )}
      </div>

      {phase === "done" && (
        <p className="rounded-xl border border-zinc-700 bg-zinc-900/60 p-4 text-sm leading-relaxed text-zinc-300">
          <strong className="text-zinc-100">How did that feel?</strong> If you were
          reading the note you were playing, you were behind. The aim is to be
          looking at the next bar while your hands finish this one. Slow the tempo
          until you can — reading ahead at a slow tempo beats scrambling at a fast
          one every time.
        </p>
      )}
    </section>
  );
}

/** Fractional MIDI → "F♯ +12¢", for the live tuner readout. Plain note names on
 *  screen: a student reads "G", never "G4". */
const NOTE_NAMES = ["C", "C♯", "D", "E♭", "E", "F", "F♯", "G", "A♭", "A", "B♭", "B"];
function midiName(m: number): string {
  const r = Math.round(m);
  const cents = Math.round((m - r) * 100);
  const pc = ((r % 12) + 12) % 12;
  return `${NOTE_NAMES[pc]}${cents === 0 ? "" : cents > 0 ? ` +${cents}¢` : ` ${cents}¢`}`;
}
